import React, { useState } from "react";
import Modal from "react-bootstrap/Modal";
import { Formik, Form, Field } from "formik";
import * as yup from "yup";
import { Form as BootstrapForm, Button, Col, Row } from "react-bootstrap";
import "./style.css"; // Custom styles for the modal
import ErrorMessageComponent from "../../modules/common/ErrorMessageComponent";
import SubmitButton from "../../modules/common/SubmitButton";
import { useDispatch } from "react-redux";
import { errorMessage } from "../../utlis/common";
import { uploadInventoryCsv } from "../../redux/features/inventory/inventoryAction";

interface InventoryModalProps {
  show: boolean;
  handleClose: () => void;
  data: any[];
  headers: string[];
}

const InventoryModal: React.FC<InventoryModalProps> = ({ show, handleClose, data, headers }) => {
  const dispatch: any = useDispatch();
  const [loading, setLoading] = useState(false);

  // Static fields for the first column
  const staticFieldsMeta = {
    "SKU": {
      key: "sku",
      description: "Identificatore univoco del prodotto. Solo lettere, numeri, trattini o underscore.",
      controls: "Obbligatorio. Lunghezza massima: 30 caratteri. Deve essere univoco.",
    },
    "EAN": {
      key: "ean",
      description: "Codice a barre esattamente come scansionato (EAN-8 o EAN-13). Solo cifre.",
      controls: "Solo 8 o 13 cifre. Nessuno spazio o simbolo.",
    },
    "Quantità": {
      key: "totalQuantity",
      description: "Numero di unità vendute. Deve essere un numero intero positivo.",
      controls: "Min: 1, Max: 1.000",
    },
    "Prezzo Vendita": {
      key: "prezzoVendita",
      description: "Prezzo di vendita unitario (IVA esclusa). Usa il punto `.` come separatore decimale.",
      controls: "Min: 0.01, Max: 10.000,00",
    },
    "Costo Articoli": {
      key: "costoArticoli",
      description: "Costo unitario per l'azienda (IVA esclusa). Deve essere ≤ al Prezzo di Vendita.",
      controls: "Min: 0.01, Max: 5.000,00. Deve essere ≤ al Prezzo di Vendita.",
    },
    "IVA": {
      key: "iva",
      description: "Percentuale IVA, scritta come numero intero (senza simbolo %).",
      controls: "Valori accettati: 0, 4, 10, 22",
    },
    "Margine previsto del prodotto (%)": {
      key: "productTargetMargin",
      description: "Inserisci la percentuale di margine che desideri ottenere su ogni vendita, da 1% a 100%. Più competitiva sarà la tua offerta e maggiori saranno le tue possibilità di ricevere ordini. Consideralo come un sistema di aste: a parità di condizioni, i rivenditori con le offerte migliori hanno la priorità. Il sistema prenderà sempre il valore più basso tra il margine previsto di prodotto e quello target dell'account.",
      controls: "",
    },
  };

  const initialValues = {
    mappedFields: Object.values(staticFieldsMeta).reduce((acc, meta) => {
      acc[meta.key] = "";
      return acc;
    }, {} as { [key: string]: string }),
  };

  const validationSchema = yup.object().shape({
    // mappedFields: yup.object().shape(
    //   Object.values(staticFieldsMeta).reduce((acc, meta) => {
    //     acc[meta.key] = ["sku", "ean", "totalQuantity"].includes(meta.key)
    //       ? yup.string().required(`${meta.key.toUpperCase()} la mappatura è obbligatoria`)
    //       : yup.string();
    //     return acc;
    //   }, {} as { [key: string]: any })
    // ),

    mappedFields: yup.object().shape(
      Object.values(staticFieldsMeta).reduce((acc, meta) => {
        if (meta.key === "sku") {
          acc[meta.key] = yup
            .string()
            .required("SKU la mappatura è obbligatoria")
            .matches(
              /^[A-Za-z0-9_-]{1,30}$/,
              "SKU può contenere solo lettere, numeri, trattini o underscore. Massimo 30 caratteri."
            );
        } else if (["ean", "totalQuantity"].includes(meta.key)) {
          acc[meta.key] = yup.string().required(`${meta.key.toUpperCase()} la mappatura è obbligatoria`);
        } else {
          acc[meta.key] = yup.string();
        }
        return acc;
      }, {} as { [key: string]: any })
    ),
  });

  const handleSubmit = async (values: typeof initialValues, { resetForm }: { resetForm: () => void }) => {
    try {
      setLoading(true);

      // Validate SKU values are integers
      const skuHeader = values.mappedFields.sku;
      if (skuHeader) {
        for (let i = 0; i < data.length; i++) {
          const skuValue = data[i][skuHeader];
          // if (skuValue === undefined || skuValue === null || !Number.isInteger(Number(skuValue)) || isNaN(Number(skuValue)) || skuValue === 0) {
          //   errorMessage(`SKU value must be an valid integer number.`);
          //   return;
          // }

          const trimmed = String(skuValue).trim();
          if (!/^[A-Za-z0-9_-]{1,30}$/.test(trimmed) || /^0+$/.test(trimmed)) {
            errorMessage(`SKU '${trimmed}' nella riga ${i + 1} non è valido. Deve contenere da 1 a 30 caratteri e può includere solo lettere, numeri, trattini o underscore.`);
            setLoading(false);
            return;
          }
        }
      }

      // Validate EAN
      const eanHeader = values.mappedFields.ean;
      if (eanHeader) {
        for (let i = 0; i < data.length; i++) {
          const eanValue = data[i][eanHeader];
          if (eanValue === undefined || eanValue === null || eanValue === "") {
            errorMessage(`EAN value cannot be empty.`);
            setLoading(false);
            return;
          }

          const eanStr = String(eanValue).trim();
          if (!/^\d+$/.test(eanStr)) {
            errorMessage(`EAN must be numeric.`);
            setLoading(false);
            return;
          }

          const isValidEAN =
            eanStr.length === 8 || // EAN-8
            eanStr.length === 12 || // UPC-A
            (eanStr.length === 13 && (/^97[89]/.test(eanStr) || true)) || // ISBN-13 or EAN-13
            eanStr.length === 14; // GTIN-14

          if (!isValidEAN) {
            errorMessage(
              `Invalid EAN format. Must be one of: EAN-8 (8), UPC-A (12), EAN-13/ISBN-13 (13), GTIN-14 (14 digits)`
            );
            setLoading(false);
            return;
          }
        }
      }

      // Validate totalQuantity values are integers
      const quantityHeader = values.mappedFields.totalQuantity;
      if (quantityHeader) {
        for (let i = 0; i < data.length; i++) {
          const quantityValue = data[i][quantityHeader];
          if (quantityValue === undefined || quantityValue === null || quantityValue === "" || !Number.isInteger(Number(quantityValue)) || isNaN(Number(quantityValue))) {
            errorMessage(`Quantity value must be an integer number.`);
            return;
          }
        }
      }

      // Validate productTargetMargin values (if present)
      const marginHeader = values.mappedFields.productTargetMargin;
      if (marginHeader) {
        for (let i = 0; i < data.length; i++) {
          let rawValue = data[i][marginHeader];

          if (rawValue === undefined || rawValue === null || rawValue === "") {
            continue; // empty is allowed
          }

          const marginValue = Number(rawValue);
          if (isNaN(marginValue) || marginValue < 0 || marginValue > 100) {
            errorMessage(
              `Il margine previsto del prodotto deve essere un numero tra 0 e 100 (valore errato nella riga ${i + 1}).`
            );
            setLoading(false);
            return;
          }
        }
      }

      // Transform data based on mappings
      const mappedData: any[] = data.map(item => {
        const mappedItem: { [key: string]: any } = {};
        Object.keys(values.mappedFields).forEach(targetField => {
          const sourceField = values.mappedFields[targetField];
          if (sourceField && item[sourceField] !== undefined) {
            mappedItem[targetField] = item[sourceField];
          }
        });
        return mappedItem;
      });

      console.log('mappedData ======== ', mappedData);

      await dispatch(uploadInventoryCsv(mappedData));
      setLoading(false);
      handleClose();
      resetForm();
    } catch (error) {
      console.error("Failed to upload inventory:", error);
      setLoading(false);
    }
  };



  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      className="custom-modal-width"
    >
      <Modal.Header closeButton className="modalHeader text-white">
        <Modal.Title>Mappa i Dati dell'Inventario</Modal.Title>
      </Modal.Header>

      <Modal.Body className="modal-body">
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ handleSubmit }) => (
            <Form noValidate onSubmit={handleSubmit}>
              <Row className="mb-3">
                <Col xs={5}>
                  <strong>Campo</strong>
                </Col>
                <Col xs={2}></Col>
                <Col xs={5}>
                  <strong>Mappa a</strong>
                </Col>
              </Row>

              {Object.entries(staticFieldsMeta).map(([label, meta], index) => (
                <Row key={index} className="mb-4 align-items-start">
                  <Col xs={5}>
                    <div className="static-field-info">
                      <BootstrapForm.Label className="form-control static-field">
                        {label}
                      </BootstrapForm.Label>
                      <small className="text-muted d-block">{meta?.description}</small>
                      <small className="text-muted d-block">{meta?.controls}</small>
                    </div>
                  </Col>

                  <Col xs={2} className="text-center">
                    <i className="bi bi-arrow-right"></i>
                  </Col>

                  <Col xs={5}>
                    <Field
                      as="select"
                      name={`mappedFields.${meta.key}`}
                      className="form-select"
                    >
                      <option value="">Seleziona un campo</option>
                      {headers?.map((header) => (
                        <option key={header} value={header}>
                          {header}
                        </option>
                      ))}
                    </Field>
                    <ErrorMessageComponent name={`mappedFields.${meta.key}`} />
                  </Col>
                </Row>
              ))}

              {/* Buttons */}
              <div className="d-flex justify-content-end mt-10">
                <Button
                  variant="secondary"
                  onClick={handleClose}
                  className="me-2 custom-close-btn"
                >
                  Chiudi
                </Button>
                <SubmitButton title="Salva Mappatura" type="submit" loading={loading} />
              </div>
            </Form>
          )}
        </Formik>
      </Modal.Body>
    </Modal>
  );
};

export default InventoryModal;
